import React, { useState } from 'react';
import {
  MapPin, Copy, Check, Package, Info, Plus, Edit2, Trash2,
  Save, X, Building, Truck
} from 'lucide-react';

// Mock address data
const warehouseAddress = {
  name: 'Marcus Johnson',
  code: 'MJ-4521',
  address: {
    line1: 'Suite MJ-4521',
    line2: '12895 NW 42nd Ave',
    city: 'Miami',
    state: 'FL',
    zip: '33054',
    country: 'USA'
  },
  phone: '+1 (305) 555-0123'
};

const deliveryAddresses = [
  {
    id: 1,
    label: 'Home',
    name: 'Marcus Johnson',
    address: '45 Hope Road, Kingston 6',
    phone: '+1 (876) 555-0123',
    default: true
  },
  {
    id: 2,
    label: 'Office',
    name: 'Marcus Johnson - TechCorp',
    address: '120 Duke Street, Kingston 10',
    phone: '+1 (876) 555-0456',
    default: false
  }
];

const pickupLocations = [
  { id: 1, name: 'Kingston Hub - Half Way Tree', address: '15 Constant Spring Road, HWT', hours: 'Mon-Fri: 9AM-6PM, Sat: 9AM-2PM' },
  { id: 2, name: 'Portmore Branch', address: 'Sovereign Centre, Portmore', hours: 'Mon-Sat: 10AM-5PM' },
  { id: 3, name: 'Montego Bay Branch', address: 'Fairview Shopping Centre', hours: 'Mon-Fri: 9AM-5PM' },
];

export default function CustomerAddressBook() {
  const [copiedField, setCopiedField] = useState(null);
  const [preferredPickup, setPreferredPickup] = useState(pickupLocations[0].id);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatFullAddress = () => {
    const { line1, line2, city, state, zip, country } = warehouseAddress.address;
    return `${warehouseAddress.name}\n${line1}\n${line2}\n${city}, ${state} ${zip}\n${country}`;
  };

  return (
    <div className="customer-address-page" data-testid="customer-address">
      {/* Florida Shipping Address */}
      <div className="shipping-address-card">
        <div className="card-header-row">
          <div className="header-with-icon">
            <Package size={24} />
            <div>
              <h2>Your Florida Shipping Address</h2>
              <p>Use this address for all your online purchases</p>
            </div>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => copyToClipboard(formatFullAddress(), 'full')}
            data-testid="copy-full-address"
          >
            {copiedField === 'full' ? <Check size={16} /> : <Copy size={16} />}
            {copiedField === 'full' ? 'Copied!' : 'Copy Address'}
          </button>
        </div>

        <div className="shipping-address-content">
          <div className="address-display">
            <div className="address-line highlight">
              <span className="label">Shipping Code</span>
              <span className="value code">{warehouseAddress.code}</span>
              <button 
                className="copy-btn"
                onClick={() => copyToClipboard(warehouseAddress.code, 'code')}
              >
                {copiedField === 'code' ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
            
            <div className="address-line">
              <span className="label">Name</span>
              <span className="value">{warehouseAddress.name}</span>
            </div>
            
            <div className="address-line">
              <span className="label">Address Line 1</span>
              <span className="value">{warehouseAddress.address.line1}</span>
            </div>
            
            <div className="address-line">
              <span className="label">Address Line 2</span>
              <span className="value">{warehouseAddress.address.line2}</span>
            </div>
            
            <div className="address-line">
              <span className="label">City, State, ZIP</span>
              <span className="value">
                {warehouseAddress.address.city}, {warehouseAddress.address.state} {warehouseAddress.address.zip}
              </span>
            </div>
            
            <div className="address-line">
              <span className="label">Country</span>
              <span className="value">{warehouseAddress.address.country}</span>
            </div>
            
            <div className="address-line">
              <span className="label">Phone</span>
              <span className="value">{warehouseAddress.phone}</span>
            </div>
          </div>

          <div className="address-instructions">
            <div className="instruction-header">
              <Info size={18} />
              <h4>How to Use This Address</h4>
            </div>
            <ol className="instruction-list">
              <li>Copy the full address above</li>
              <li>Use it as your shipping address on Amazon, eBay, or any US store</li>
              <li>Include your shipping code <strong>{warehouseAddress.code}</strong> in Address Line 1</li>
              <li>We'll receive your package and ship it to Jamaica</li>
              <li>Track your package using your tracking number</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Delivery Addresses */}
      <div className="delivery-addresses-section">
        <div className="section-header-row">
          <div>
            <h2>Jamaica Delivery Addresses</h2>
            <p>Manage your delivery locations for home delivery</p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
            data-testid="add-address-btn"
          >
            <Plus size={16} /> Add Address
          </button>
        </div>

        <div className="delivery-addresses-grid">
          {deliveryAddresses.map((addr) => (
            <div 
              key={addr.id} 
              className={`delivery-address-card ${addr.default ? 'default' : ''}`}
              data-testid={`delivery-${addr.id}`}
            >
              <div className="address-card-header">
                <div className="address-label">
                  <MapPin size={16} />
                  <span>{addr.label}</span>
                  {addr.default && <span className="default-badge">Default</span>}
                </div>
                <div className="address-actions">
                  <button 
                    className="action-btn"
                    onClick={() => setEditingAddress(addr)}
                    data-testid={`edit-${addr.id}`}
                  >
                    <Edit2 size={14} />
                  </button>
                  {!addr.default && (
                    <button className="action-btn delete" data-testid={`delete-${addr.id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="address-card-body">
                <p className="address-name">{addr.name}</p>
                <p className="address-text">{addr.address}</p>
                <p className="address-phone">{addr.phone}</p>
              </div>
              {!addr.default && (
                <button className="btn btn-secondary btn-sm set-default">
                  Set as Default
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Preferred Pickup Location */}
      <div className="pickup-locations-section">
        <div className="section-header">
          <Building size={24} />
          <div>
            <h2>Preferred Pickup Location</h2>
            <p>Select your preferred branch for package pickup</p>
          </div>
        </div>

        <div className="pickup-locations-list">
          {pickupLocations.map((location) => (
            <div 
              key={location.id}
              className={`pickup-location-card ${preferredPickup === location.id ? 'selected' : ''}`}
              onClick={() => setPreferredPickup(location.id)}
              data-testid={`pickup-${location.id}`}
            >
              <div className="location-radio">
                <div className={`radio-circle ${preferredPickup === location.id ? 'checked' : ''}`}>
                  {preferredPickup === location.id && <Check size={12} />}
                </div>
              </div>
              <div className="location-info">
                <h4>{location.name}</h4>
                <p className="location-address">{location.address}</p>
                <p className="location-hours">{location.hours}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Address Modal */}
      {(showAddModal || editingAddress) && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setEditingAddress(null); }}>
          <div 
            className="modal-content address-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="address-modal"
          >
            <div className="modal-header">
              <h3>{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>
              <button 
                className="close-btn" 
                onClick={() => { setShowAddModal(false); setEditingAddress(null); }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Address Label</label>
                <input 
                  type="text" 
                  placeholder="e.g., Home, Office, Mom's House"
                  defaultValue={editingAddress?.label || ''}
                  data-testid="address-label-input"
                />
              </div>

              <div className="form-group">
                <label>Recipient Name</label>
                <input 
                  type="text" 
                  placeholder="Full name"
                  defaultValue={editingAddress?.name || ''}
                  data-testid="recipient-name-input"
                />
              </div>

              <div className="form-group">
                <label>Street Address</label>
                <textarea 
                  placeholder="Street address, apartment, unit, etc."
                  rows={2}
                  defaultValue={editingAddress?.address || ''}
                  data-testid="street-address-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City/Parish</label>
                  <input 
                    type="text" 
                    placeholder="e.g., Kingston"
                    data-testid="city-input"
                  />
                </div>
                <div className="form-group">
                  <label>Postal Code</label>
                  <input 
                    type="text" 
                    placeholder="e.g., KGN 6"
                    data-testid="postal-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input 
                  type="tel" 
                  placeholder="+1 (876) 555-0000"
                  defaultValue={editingAddress?.phone || ''}
                  data-testid="phone-input"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    defaultChecked={editingAddress?.default || false}
                    data-testid="default-checkbox"
                  />
                  <span>Set as default delivery address</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => { setShowAddModal(false); setEditingAddress(null); }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="save-address-btn">
                <Save size={14} /> Save Address
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
